import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { endOfDay, format, parseISO, startOfYear } from 'date-fns';
import { useAction, useQuery } from 'convex/react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import { countQuotaInvoices } from '@bill-alps/backend/features/bills/quota';
import { PLAN_LIMITS } from '@bill-alps/backend/shared/billing/planLimits';
import { getModulePlan } from '@bill-alps/backend/shared/billing/moduleEntitlements';
import { isModuleEnabled } from '@bill-alps/backend/features/companies/modules';
import {
  TrendingUp,
  AlertCircle,
  Clock,
  Building2,
  FileText,
  FileSignature,
  Users,
  Check,
  ArrowRight,
  UserRound,
  Receipt,
  Send,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Select,
} from '@bill-alps/ui';
import type { DateRangeValue } from '@bill-alps/ui';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useClientMap } from '@/hooks/use-client-map';
import { useTranslation, useLanguage } from '../../i18n/useTranslation';
import { PageErrorBoundary } from '@/components/page-error-boundary';
import { PageHeading, PageHeadingDescription } from '@/components/page-heading';
import { formatCurrency } from '@/lib/format/currency';
import { formatDate } from '@/lib/format/date';
import { PageLoader } from '@/components/page-loader';
import { DEFAULT_ACCENT, SERIES_COLORS } from './lib/chart-series';
import { DashboardChartCardSkeleton } from './components/dashboard-chart-card-skeleton';
import { DashboardChartControls } from './components/dashboard-chart-controls';
import { DashboardGraphSelector } from './components/dashboard-graph-selector';
import { buildMultiValueSeries, buildValueSeries } from './lib/time-series';
import type { ChartGranularity } from './lib/time-series';
import {
  buildClientBillPieSeries,
  buildEmployeePayslipPieSeries,
  MAX_RATE_MONTHS,
} from './lib/pie-series';
import { tsToYearMonth } from '../bills/lib/vat-declaration';
import {
  DASHBOARD_GRAPH_IDS,
  dashboardGraphStorageKey,
  parseStoredGraphSelection,
} from './lib/graph-selection';
import type { DashboardGraphId } from './lib/graph-selection';
import type { Doc } from '@bill-alps/backend/convex/_generated/dataModel';

// recharts is ~744 KB and the dashboard is the landing route: keep it off the
// critical path and swap it in behind a same-height skeleton.
const DashboardChartCard = lazy(async () => ({
  default: (await import('./components/dashboard-chart-card'))
    .DashboardChartCard,
}));
const DashboardPieChartCard = lazy(async () => ({
  default: (await import('./components/dashboard-pie-chart-card'))
    .DashboardPieChartCard,
}));

const CRITICAL_FIELDS = ['address', 'bankIban', 'email'] as const;
const RECOMMENDED_FIELDS = ['phone', 'vatNumber', 'ide'] as const;

// Sentinel for the currency Select; lowercase cannot collide with ISO 4217.
const ALL_CURRENCIES = 'all';

type PayslipPiePeriod = 'year' | 'month';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function DashboardPageContent() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { language } = useLanguage();
  const { user, isLoading: isUserLoading, canReadModule } = useCurrentUser();
  const companyId = user?.company?._id;
  // The badge shows the subscription itself, so it keeps the company plan.
  const plan = user?.company?.plan ?? 'free';
  // Per-module limits: a single-module subscription leaves the other module on
  // free-plan limits, so the invoicing and payroll cards can disagree.
  const invoicingLimits =
    PLAN_LIMITS[getModulePlan(user?.company, 'invoicing')];
  const payrollLimits = PLAN_LIMITS[getModulePlan(user?.company, 'payroll')];
  const invoicingEnabled =
    isModuleEnabled(user?.company, 'invoicing') && canReadModule('invoicing');
  const payrollEnabled =
    isModuleEnabled(user?.company, 'payroll') && canReadModule('payroll');

  const bills = useQuery(
    api.features.bills.invoices.getByCompany,
    companyId && invoicingEnabled ? { companyId } : 'skip',
  );
  const clients = useQuery(
    api.features.clients.records.getByCompany,
    companyId && invoicingEnabled ? { companyId } : 'skip',
  );
  const quotes = useQuery(
    api.features.quotes.documents.getByCompany,
    companyId && invoicingEnabled ? { companyId } : 'skip',
  );
  const employees = useQuery(
    api.features.employees.records.getByCompany,
    companyId && payrollEnabled ? { companyId } : 'skip',
  );
  const payslips = useQuery(
    api.features.payslips.records.getByCompany,
    companyId && payrollEnabled ? { companyId } : 'skip',
  );

  // -- Charts --

  const [range, setRange] = useState<DateRangeValue>(() => ({
    from: `${new Date().getFullYear()}-01-01`,
    to: format(new Date(), 'yyyy-MM-dd'),
  }));
  const [granularity, setGranularity] = useState<ChartGranularity>('month');
  const [cumulative, setCumulative] = useState(false);
  const [chartCurrency, setChartCurrency] = useState<string>(ALL_CURRENCIES);
  const [clientBillCurrency, setClientBillCurrency] = useState<string>();
  const [payslipPiePeriod, setPayslipPiePeriod] =
    useState<PayslipPiePeriod>('year');
  const [graphSelectionOverrides, setGraphSelectionOverrides] = useState<
    Record<string, DashboardGraphId[]>
  >({});

  const graphPreferenceKey = companyId
    ? dashboardGraphStorageKey(companyId)
    : null;
  const visibleGraphs = useMemo(() => {
    if (!graphPreferenceKey) return [...DASHBOARD_GRAPH_IDS];
    if (
      Object.prototype.hasOwnProperty.call(
        graphSelectionOverrides,
        graphPreferenceKey,
      )
    ) {
      return graphSelectionOverrides[graphPreferenceKey];
    }
    try {
      return parseStoredGraphSelection(
        localStorage.getItem(graphPreferenceKey),
      );
    } catch {
      return [...DASHBOARD_GRAPH_IDS];
    }
  }, [graphPreferenceKey, graphSelectionOverrides]);

  const handleVisibleGraphsChange = (next: DashboardGraphId[]) => {
    if (!graphPreferenceKey) return;
    setGraphSelectionOverrides((current) => ({
      ...current,
      [graphPreferenceKey]: next,
    }));
    try {
      localStorage.setItem(graphPreferenceKey, JSON.stringify(next));
    } catch {
      // The in-memory selection still works when storage is unavailable.
    }
  };

  const { chartFrom, chartTo } = useMemo(() => {
    const now = new Date();
    let from = range.from ? parseISO(range.from) : startOfYear(now);
    let to = range.to ? endOfDay(parseISO(range.to)) : now;
    if (from > to) {
      from = startOfYear(now);
      to = now;
    }
    return { chartFrom: from, chartTo: to };
  }, [range]);

  const { revenueRows, revenueSeries, revenueCurrencies, revenueSelection } =
    useMemo(() => {
      const excluded = new Set(['cancelled', 'draft']);
      const inRange = (bills ?? []).filter((b) => {
        if (excluded.has(b.status)) return false;
        const d = new Date(b.issueDate);
        return d >= chartFrom && d <= chartTo;
      });

      const counts = new Map<string, number>();
      for (const b of inRange) {
        counts.set(b.currency, (counts.get(b.currency) ?? 0) + 1);
      }
      const currencies = [...counts.keys()].sort();

      // Colors are assigned over the company's full currency history so that
      // narrowing the date range never repaints the surviving series.
      const universe: string[] = [
        ...new Set((bills ?? []).map((b) => b.currency)),
      ].sort();
      const colorOf = new Map(
        universe.map((c, i) => [c, SERIES_COLORS[i % SERIES_COLORS.length]]),
      );

      let selection =
        chartCurrency !== ALL_CURRENCIES && counts.has(chartCurrency)
          ? chartCurrency
          : ALL_CURRENCIES;
      if (selection === ALL_CURRENCIES && currencies.length <= 1) {
        selection = currencies[0] ?? 'CHF';
      }

      const chartOpts = {
        from: chartFrom,
        to: chartTo,
        cumulative,
        language,
      };

      if (selection === ALL_CURRENCIES) {
        const grouped = Object.fromEntries(
          currencies.map((c) => [c, [] as { date: Date; value: number }[]]),
        );
        for (const b of inRange) {
          grouped[b.currency].push({
            date: new Date(b.issueDate),
            value: b.total,
          });
        }
        return {
          revenueRows: buildMultiValueSeries(grouped, granularity, chartOpts),
          revenueSeries: currencies.map((c) => ({
            dataKey: c,
            color: colorOf.get(c) ?? DEFAULT_ACCENT,
            currency: c,
          })),
          revenueCurrencies: currencies,
          revenueSelection: ALL_CURRENCIES,
        };
      }

      return {
        revenueRows: buildValueSeries(
          inRange
            .filter((b) => b.currency === selection)
            .map((b) => ({ date: new Date(b.issueDate), value: b.total })),
          granularity,
          chartOpts,
        ),
        revenueSeries: [
          { dataKey: 'value', color: DEFAULT_ACCENT, currency: selection },
        ],
        revenueCurrencies: currencies,
        revenueSelection: selection,
      };
    }, [
      bills,
      chartFrom,
      chartTo,
      granularity,
      cumulative,
      chartCurrency,
      language,
    ]);

  const payrollSeries = useMemo(
    () =>
      buildValueSeries(
        (payslips ?? []).map((p) => ({
          date: new Date(p.year, p.month - 1, 1),
          value: p.totalGross,
        })),
        granularity,
        { from: chartFrom, to: chartTo, cumulative, language },
      ),
    [payslips, chartFrom, chartTo, granularity, cumulative, language],
  );

  const clientBillCurrencies = useMemo<string[]>(
    () =>
      [
        ...new Set(
          (bills ?? [])
            .filter(
              (bill) => bill.status !== 'draft' && bill.status !== 'cancelled',
            )
            .map((bill) => bill.currency),
        ),
      ].sort(),
    [bills],
  );
  const companyDefaultCurrency = user?.company?.defaultCurrency;
  // Several currencies: default to all of them at once (converted to CHF), an
  // explicit pick wins for as long as that currency still has bills.
  const clientBillSelection =
    clientBillCurrencies.length > 1
      ? clientBillCurrency && clientBillCurrencies.includes(clientBillCurrency)
        ? clientBillCurrency
        : ALL_CURRENCIES
      : (clientBillCurrencies[0] ?? companyDefaultCurrency ?? 'CHF');
  const isAllClientBillCurrencies = clientBillSelection === ALL_CURRENCIES;

  // Issue months of the foreign-currency bills to convert. Newest first: the
  // backend truncates the list to MAX_RATE_MONTHS.
  const clientBillRateMonths = useMemo<string[]>(() => {
    if (!isAllClientBillCurrencies) return [];
    const months = new Set<string>();
    for (const bill of bills ?? []) {
      if (bill.status === 'draft' || bill.status === 'cancelled') continue;
      if (bill.currency === 'CHF') continue;
      const issuedAt = new Date(bill.issueDate);
      if (issuedAt < chartFrom || issuedAt > chartTo) continue;
      months.add(tsToYearMonth(bill.issueDate));
    }
    return [...months].sort().reverse().slice(0, MAX_RATE_MONTHS);
  }, [bills, chartFrom, chartTo, isAllClientBillCurrencies]);

  const clientBillRates = useQuery(
    api.features.bills.exchangeRates.getRatesForMonths,
    companyId && clientBillRateMonths.length > 0
      ? { companyId, yearMonths: clientBillRateMonths }
      : 'skip',
  );
  const isClientBillRatesLoading =
    clientBillRateMonths.length > 0 && clientBillRates === undefined;

  const ensureRates = useAction(api.features.bills.exchangeRates.ensureRates);
  const ensuredMonthsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!companyId || !clientBillRates) return;
    const cached = new Set(clientBillRates.map((rate) => rate.yearMonth));
    const missing = clientBillRateMonths.filter((month) => !cached.has(month));
    if (missing.length === 0) return;
    // The dashboard is the landing route, so only ever backfill months the
    // monthly cron has not cached — never on a normal visit. Keying the guard
    // on the month list keeps a never-published month from looping.
    const key = `${companyId}|${missing.join(',')}`;
    if (ensuredMonthsRef.current === key) return;
    ensuredMonthsRef.current = key;
    void ensureRates({ companyId, yearMonths: missing }).catch(() => {
      // Bills left without a rate are reported as excluded under the chart
    });
  }, [companyId, clientBillRates, clientBillRateMonths, ensureRates]);

  // -- Derived data --

  const { totalRevenue, totalUnpaid, oldestUnpaid, invoicesThisMonth } =
    useMemo(() => {
      const revenue = new Map<string, number>();
      const unpaid = new Map<string, number>();
      let oldest: Doc<'bills'> | null = null;

      if (bills) {
        const excluded = new Set(['paid', 'cancelled', 'draft']);

        for (const b of bills) {
          revenue.set(b.currency, (revenue.get(b.currency) ?? 0) + b.total);

          if (!excluded.has(b.status)) {
            unpaid.set(b.currency, (unpaid.get(b.currency) ?? 0) + b.total);
            if (!oldest || b.issueDate < oldest.issueDate) oldest = b;
          }
        }
      }

      return {
        totalRevenue: revenue,
        totalUnpaid: unpaid,
        oldestUnpaid: oldest,
        // What the create guard counts: this UTC month, imports excluded.
        invoicesThisMonth: countQuotaInvoices(
          bills ?? [],
          new Date().getTime(),
        ),
      };
    }, [bills]);

  const clientMap = useClientMap(clients);

  const clientBillPie = useMemo(
    () =>
      buildClientBillPieSeries({
        bills: bills ?? [],
        clientLabels: clientMap,
        from: chartFrom,
        to: chartTo,
        currency: isAllClientBillCurrencies ? null : clientBillSelection,
        rates: clientBillRates ?? [],
        otherLabel: t('dashboard:charts.other'),
        language,
      }),
    [
      bills,
      clientMap,
      chartFrom,
      chartTo,
      isAllClientBillCurrencies,
      clientBillSelection,
      clientBillRates,
      t,
      language,
    ],
  );

  const employeeMap = useMemo(
    () =>
      new Map(
        (employees ?? []).map((employee) => [
          employee._id,
          `${employee.firstName} ${employee.lastName}`,
        ]),
      ),
    [employees],
  );
  const payslipPieNow = new Date();
  const payslipPieYear = payslipPieNow.getFullYear();
  const payslipPieMonth = payslipPieNow.getMonth() + 1;
  const employeePayslipPieSeries = useMemo(
    () =>
      buildEmployeePayslipPieSeries({
        payslips: payslips ?? [],
        employeeLabels: employeeMap,
        year: payslipPieYear,
        month: payslipPiePeriod === 'month' ? payslipPieMonth : undefined,
        otherLabel: t('dashboard:charts.other'),
        language,
      }),
    [
      payslips,
      employeeMap,
      payslipPieYear,
      payslipPieMonth,
      payslipPiePeriod,
      t,
      language,
    ],
  );

  const { openQuotesCount, openQuotesValue, acceptanceRate } = useMemo(() => {
    const value = new Map<string, number>();
    let openCount = 0;
    let accepted = 0;
    let declined = 0;

    for (const q of quotes ?? []) {
      if (q.status === 'draft' || q.status === 'sent') {
        openCount++;
        value.set(q.currency, (value.get(q.currency) ?? 0) + q.total);
      } else if (q.status === 'accepted') {
        accepted++;
      } else if (q.status === 'declined') {
        declined++;
      }
    }

    return {
      openQuotesCount: openCount,
      openQuotesValue: value,
      // null when no client has decided yet (guards division by zero)
      acceptanceRate:
        accepted + declined === 0
          ? null
          : Math.round((accepted / (accepted + declined)) * 100),
    };
  }, [quotes]);

  const {
    payslipsThisMonth,
    currentPeriod,
    prevPeriod,
    currentPeriodLabel,
    prevPeriodLabel,
  } = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;

    const formatPeriod = (year: number, month: number) =>
      new Date(year, month - 1).toLocaleString(language, {
        month: 'long',
        year: 'numeric',
      });

    let payslipsThisMonthCount = 0;
    const current = { total: 0, sent: 0 };
    const prev = { total: 0, sent: 0 };

    for (const p of payslips ?? []) {
      const d = new Date(p._creationTime);
      if (
        d.getFullYear() === currentYear &&
        d.getMonth() + 1 === currentMonth
      ) {
        payslipsThisMonthCount++;
      }
      if (p.year === currentYear && p.month === currentMonth) {
        current.total++;
        if (p.sentStatus) current.sent++;
      }
      if (p.year === prevYear && p.month === prevMonth) {
        prev.total++;
        if (p.sentStatus) prev.sent++;
      }
    }

    return {
      payslipsThisMonth: payslipsThisMonthCount,
      currentPeriod: current,
      prevPeriod: prev,
      currentPeriodLabel: formatPeriod(currentYear, currentMonth),
      prevPeriodLabel: formatPeriod(prevYear, prevMonth),
    };
  }, [payslips, language]);

  const missingFields = useMemo(() => {
    const company = user?.company;
    if (!company)
      return {
        critical: [...CRITICAL_FIELDS],
        recommended: [...RECOMMENDED_FIELDS],
      };
    const critical = CRITICAL_FIELDS.filter(
      (f) => !company[f as keyof typeof company],
    );
    const recommended = RECOMMENDED_FIELDS.filter(
      (f) => !company[f as keyof typeof company],
    );
    return { critical, recommended };
  }, [user?.company]);

  const graphOptions: { value: DashboardGraphId; label: string }[] = [];
  if (invoicingEnabled) {
    graphOptions.push(
      { value: 'revenue', label: t('dashboard:charts.revenue') },
      { value: 'clientBills', label: t('dashboard:charts.clientBills') },
    );
  }
  if (payrollEnabled) {
    graphOptions.push(
      { value: 'payroll', label: t('dashboard:charts.payroll') },
      {
        value: 'employeePayslips',
        label: t('dashboard:charts.employeePayslips'),
      },
    );
  }

  const visibleGraphSet = new Set(visibleGraphs);
  const showRevenue = invoicingEnabled && visibleGraphSet.has('revenue');
  const showPayroll = payrollEnabled && visibleGraphSet.has('payroll');
  const showClientBills =
    invoicingEnabled && visibleGraphSet.has('clientBills');
  const showEmployeePayslips =
    payrollEnabled && visibleGraphSet.has('employeePayslips');
  const selectedAvailableGraphCount = graphOptions.filter((option) =>
    visibleGraphSet.has(option.value),
  ).length;
  const showDateRangeControls = showRevenue || showPayroll || showClientBills;
  const showTimelineControls = showRevenue || showPayroll;
  const showAnyGraph =
    showRevenue || showPayroll || showClientBills || showEmployeePayslips;

  if (isUserLoading) {
    return <PageLoader />;
  }

  const allFieldsComplete =
    missingFields.critical.length === 0 &&
    missingFields.recommended.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <PageHeading className="text-3xl font-bold">
            {t('dashboard:title')}
          </PageHeading>
          <Badge variant={plan !== 'free' ? 'pink' : 'teal'}>
            {t(`dashboard:plan.${plan}`)}
          </Badge>
        </div>
        <PageHeadingDescription>
          {t('dashboard:description')}
        </PageHeadingDescription>
      </div>

      {(invoicingEnabled || payrollEnabled) && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <DashboardGraphSelector
              options={graphOptions}
              value={visibleGraphs}
              onChange={handleVisibleGraphsChange}
              label={t('dashboard:charts.graphs')}
              selectionSummary={t('dashboard:charts.graphSelection', {
                selected: selectedAvailableGraphCount,
                total: graphOptions.length,
              })}
            />
            {(showDateRangeControls || showTimelineControls) && (
              <DashboardChartControls
                range={range}
                onRangeChange={setRange}
                granularity={granularity}
                onGranularityChange={setGranularity}
                cumulative={cumulative}
                onCumulativeChange={setCumulative}
                showDateRange={showDateRangeControls}
                showTimelineControls={showTimelineControls}
              />
            )}
          </div>

          {showAnyGraph && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {showRevenue && (
                <Suspense
                  fallback={
                    <DashboardChartCardSkeleton
                      title={t('dashboard:charts.revenue')}
                    />
                  }
                >
                  <DashboardChartCard
                    title={t('dashboard:charts.revenue')}
                    data={revenueRows}
                    series={revenueSeries}
                    cumulative={cumulative}
                    emptyLabel={t('dashboard:charts.empty')}
                    headerExtra={
                      revenueCurrencies.length > 1 ? (
                        <Select
                          size="sm"
                          className="w-24"
                          options={[
                            {
                              value: ALL_CURRENCIES,
                              label: t('dashboard:charts.allCurrencies'),
                            },
                            ...revenueCurrencies.map((c) => ({
                              value: c,
                              label: c,
                            })),
                          ]}
                          value={revenueSelection}
                          onChange={setChartCurrency}
                        />
                      ) : undefined
                    }
                  />
                </Suspense>
              )}
              {showPayroll && (
                <Suspense
                  fallback={
                    <DashboardChartCardSkeleton
                      title={t('dashboard:charts.payroll')}
                    />
                  }
                >
                  <DashboardChartCard
                    title={t('dashboard:charts.payroll')}
                    data={payrollSeries}
                    series={[
                      {
                        dataKey: 'value',
                        color: 'var(--color-riso-teal, #157a6e)',
                        currency: 'CHF',
                      },
                    ]}
                    cumulative={cumulative}
                    emptyLabel={t('dashboard:charts.empty')}
                  />
                </Suspense>
              )}
              {showClientBills && (
                <Suspense
                  fallback={
                    <DashboardChartCardSkeleton
                      title={t('dashboard:charts.clientBills')}
                    />
                  }
                >
                  <DashboardPieChartCard
                    title={t('dashboard:charts.clientBills')}
                    data={clientBillPie.slices}
                    currency={
                      isAllClientBillCurrencies ? 'CHF' : clientBillSelection
                    }
                    emptyLabel={t('dashboard:charts.empty')}
                    notice={
                      clientBillPie.excludedBillCount > 0 &&
                      !isClientBillRatesLoading
                        ? t('dashboard:charts.excludedBills', {
                            count: clientBillPie.excludedBillCount,
                          })
                        : undefined
                    }
                    headerExtra={
                      clientBillCurrencies.length > 1 ? (
                        <Select
                          size="sm"
                          className="w-24"
                          aria-label={t('dashboard:charts.currency')}
                          options={[
                            {
                              value: ALL_CURRENCIES,
                              label: t('dashboard:charts.allCurrencies'),
                            },
                            ...clientBillCurrencies.map((currency) => ({
                              value: currency,
                              label: currency,
                            })),
                          ]}
                          value={clientBillSelection}
                          onChange={setClientBillCurrency}
                        />
                      ) : undefined
                    }
                  />
                </Suspense>
              )}
              {showEmployeePayslips && (
                <Suspense
                  fallback={
                    <DashboardChartCardSkeleton
                      title={t('dashboard:charts.employeePayslips')}
                    />
                  }
                >
                  <DashboardPieChartCard
                    title={t('dashboard:charts.employeePayslips')}
                    data={employeePayslipPieSeries}
                    currency="CHF"
                    emptyLabel={t('dashboard:charts.empty')}
                    headerExtra={
                      <Select
                        size="sm"
                        className="w-36"
                        aria-label={t('dashboard:charts.period')}
                        options={[
                          {
                            value: 'year',
                            label: t('dashboard:charts.currentYear'),
                          },
                          {
                            value: 'month',
                            label: t('dashboard:charts.currentMonth'),
                          },
                        ]}
                        value={payslipPiePeriod}
                        onChange={(value) =>
                          setPayslipPiePeriod(value as PayslipPiePeriod)
                        }
                      />
                    }
                  />
                </Suspense>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {invoicingEnabled && (
          /* 1. Total revenue */
          <Card variant="accent-teal" static>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="h-5 w-5" />
                {t('dashboard:totalRevenue.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {totalRevenue.size === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('dashboard:totalRevenue.empty')}
                </p>
              ) : (
                <div className="space-y-1">
                  {[...totalRevenue.entries()].map(([currency, amount]) => (
                    <p key={currency} className="text-2xl font-bold">
                      {formatCurrency(amount, currency)}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {invoicingEnabled && (
          /* 2. Total unpaid */
          <Card variant="accent-pink" static>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertCircle className="h-5 w-5" />
                {t('dashboard:totalUnpaid.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {totalUnpaid.size === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('dashboard:totalUnpaid.empty')}
                </p>
              ) : (
                <div className="space-y-1">
                  {[...totalUnpaid.entries()].map(([currency, amount]) => (
                    <p key={currency} className="text-2xl font-bold">
                      {formatCurrency(amount, currency)}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {invoicingEnabled && (
          /* 3. Oldest unpaid invoice */
          <Card static>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5" />
                {t('dashboard:oldestUnpaid.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!oldestUnpaid ? (
                <p className="text-sm text-muted-foreground">
                  {t('dashboard:oldestUnpaid.empty')}
                </p>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold">
                      {t('dashboard:oldestUnpaid.invoiceFrom', {
                        invoiceNumber: oldestUnpaid.invoiceNumber,
                        client: clientMap.get(oldestUnpaid.clientId) ?? '—',
                      })}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard:oldestUnpaid.issuedOn', {
                        date: formatDate(oldestUnpaid.issueDate),
                      })}
                    </p>
                    <p className="mt-1 text-lg font-bold">
                      {formatCurrency(
                        oldestUnpaid.total,
                        oldestUnpaid.currency,
                      )}
                    </p>
                  </div>
                  <Link
                    to={`/bills/${encodeURIComponent(oldestUnpaid.invoiceNumber)}`}
                    className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-4 hover:text-foreground/80"
                  >
                    {t('dashboard:oldestUnpaid.viewInvoice')}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 4. Missing information */}
        <Card static>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5" />
              {t('dashboard:missingInfo.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {allFieldsComplete ? (
              <p className="flex items-center gap-2 text-sm font-medium text-riso-teal">
                <Check className="h-4 w-4" />
                {t('dashboard:missingInfo.allComplete')}
              </p>
            ) : (
              <div className="space-y-3">
                {missingFields.critical.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('dashboard:missingInfo.critical')}
                    </p>
                    <ul className="space-y-1">
                      {missingFields.critical.map((field) => (
                        <li
                          key={field}
                          className="flex items-center gap-2 text-sm"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-riso-pink" />
                          {t(`dashboard:missingInfo.fields.${field}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {missingFields.recommended.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('dashboard:missingInfo.recommended')}
                    </p>
                    <ul className="space-y-1">
                      {missingFields.recommended.map((field) => (
                        <li
                          key={field}
                          className="flex items-center gap-2 text-sm"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-black" />
                          {t(`dashboard:missingInfo.fields.${field}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <Link
                  to="/company"
                  className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-4 hover:text-foreground/80"
                >
                  {t('dashboard:missingInfo.goToSettings')}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {invoicingEnabled && (
          /* 5. Invoices this month */
          <Card variant="accent-blue" static data-testid="quota-invoices">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5" />
                {t('dashboard:invoicesThisMonth.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {invoicingLimits.invoicesPerMonth === Infinity
                  ? t('dashboard:invoicesThisMonth.countUnlimited', {
                      count: invoicesThisMonth,
                    })
                  : t('dashboard:invoicesThisMonth.count', {
                      count: invoicesThisMonth,
                      limit: invoicingLimits.invoicesPerMonth,
                    })}
              </p>
              {invoicingLimits.invoicesPerMonth !== Infinity && (
                <div className="mt-3">
                  <div className="h-3 w-full overflow-hidden rounded-full border-2 border-border bg-muted">
                    <div
                      className="h-full rounded-full bg-riso-blue transition-all"
                      style={{
                        width: `${Math.min((invoicesThisMonth / invoicingLimits.invoicesPerMonth) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('dashboard:invoicesThisMonth.limit')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {invoicingEnabled && (
          /* 5b. Open quotes */
          <Card static>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileSignature className="h-5 w-5" />
                {t('dashboard:quotes.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-2xl font-bold">
                    {t('dashboard:quotes.openCount', {
                      count: openQuotesCount,
                    })}
                  </p>
                  {openQuotesValue.size > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {[...openQuotesValue.entries()].map(
                        ([currency, amount]) => (
                          <p
                            key={currency}
                            className="text-sm text-muted-foreground"
                          >
                            {formatCurrency(amount, currency)}
                          </p>
                        ),
                      )}
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {acceptanceRate === null
                    ? t('dashboard:quotes.noDecisions')
                    : t('dashboard:quotes.acceptanceRate', {
                        rate: acceptanceRate,
                      })}
                </p>
                <Link
                  to="/quotes"
                  className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-4 hover:text-foreground/80"
                >
                  {t('dashboard:quotes.viewQuotes')}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {invoicingEnabled && (
          /* 6. Clients */
          <Card variant="accent-amber" static data-testid="quota-clients">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5" />
                {t('dashboard:clients.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {invoicingLimits.clients === Infinity
                  ? t('dashboard:clients.countUnlimited', {
                      count: clients?.length ?? 0,
                    })
                  : t('dashboard:clients.count', {
                      count: clients?.length ?? 0,
                      limit: invoicingLimits.clients,
                    })}
              </p>
              {invoicingLimits.clients !== Infinity && (
                <div className="mt-3">
                  <div className="h-3 w-full overflow-hidden rounded-full border-2 border-border bg-muted">
                    <div
                      className="h-full rounded-full bg-riso-amber transition-all"
                      style={{
                        width: `${Math.min(((clients?.length ?? 0) / invoicingLimits.clients) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('dashboard:clients.limit')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {payrollEnabled && (
          /* 7. Employees */
          <Card static data-testid="quota-employees">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserRound className="h-5 w-5" />
                {t('dashboard:employees.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {payrollLimits.employees === Infinity
                  ? t('dashboard:employees.countUnlimited', {
                      count: employees?.length ?? 0,
                    })
                  : t('dashboard:employees.count', {
                      count: employees?.length ?? 0,
                      limit: payrollLimits.employees,
                    })}
              </p>
              {payrollLimits.employees !== Infinity && (
                <div className="mt-3">
                  <div className="h-3 w-full overflow-hidden rounded-full border-2 border-border bg-muted">
                    <div
                      className="h-full rounded-full bg-riso-teal transition-all"
                      style={{
                        width: `${Math.min(((employees?.length ?? 0) / payrollLimits.employees) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('dashboard:employees.limit')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {payrollEnabled && (
          /* 8. Payslips this month */
          <Card static data-testid="quota-payslips">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Receipt className="h-5 w-5" />
                {t('dashboard:payslipsThisMonth.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {payrollLimits.payslipsPerMonth === Infinity
                  ? t('dashboard:payslipsThisMonth.countUnlimited', {
                      count: payslipsThisMonth,
                    })
                  : t('dashboard:payslipsThisMonth.count', {
                      count: payslipsThisMonth,
                      limit: payrollLimits.payslipsPerMonth,
                    })}
              </p>
              {payrollLimits.payslipsPerMonth !== Infinity && (
                <div className="mt-3">
                  <div className="h-3 w-full overflow-hidden rounded-full border-2 border-border bg-muted">
                    <div
                      className="h-full rounded-full bg-riso-blue transition-all"
                      style={{
                        width: `${Math.min((payslipsThisMonth / payrollLimits.payslipsPerMonth) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('dashboard:payslipsThisMonth.limit')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {payrollEnabled && (
          /* 9. Payslips sent */
          <Card static>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Send className="h-5 w-5" />
                {t('dashboard:payslipsSent.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium capitalize">
                    {currentPeriodLabel}
                  </p>
                  {currentPeriod.total === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard:payslipsSent.empty')}
                    </p>
                  ) : (
                    <p className="text-sm font-bold">
                      {t('dashboard:payslipsSent.sent', {
                        sent: currentPeriod.sent,
                        total: currentPeriod.total,
                      })}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium capitalize">
                    {prevPeriodLabel}
                  </p>
                  {prevPeriod.total === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard:payslipsSent.empty')}
                    </p>
                  ) : (
                    <p className="text-sm font-bold">
                      {t('dashboard:payslipsSent.sent', {
                        sent: prevPeriod.sent,
                        total: prevPeriod.total,
                      })}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export function DashboardPage() {
  return (
    <PageErrorBoundary>
      <DashboardPageContent />
    </PageErrorBoundary>
  );
}
